import * as angular from 'angular';
import * as _ from 'lodash';

import { IAccountService, ILoggerService, IUserService, IAuthenticationService, IStoreService } from '../../../services';
import { Miniature, Chronicle, Record, ComponentDefinition, ContextualField, PropertyType, IUserViewModel } from '../../../models';
import { IUserFactory } from '../../../states';
import { Helper, guidEmpty } from '../../../common';
import { validateEmailList } from '../../../common/helpers/validateEmailList';
import { StoreElement } from '../../../stateManagement';

class UserContextualController implements angular.IComponentController {
    formController: any;
    modernDesign: boolean;
    selectedItem: IUserViewModel;
    userPicture: FileList;
    userPictureBase64: string;
    userPictureName: string;
    userSignature: any;
    userSignatureBase64: string;
    userSignatureName: string;
    users: IUserViewModel[] = [];
    usersTypes: Miniature[];
    guidEmpty = guidEmpty;

    companyField: ContextualField;
    datePickerFormatField: ContextualField;
    departmentField: ContextualField;
    domainNameField: ContextualField;
    emailField: ContextualField;
    firstNameField: ContextualField;
    initialsField: ContextualField;
    isSuperAdminField: ContextualField;
    jobTitleField: ContextualField;
    lastNameField: ContextualField;
    phoneField: ContextualField;
    pictureField: ContextualField;
    signatureField: ContextualField;
    titleField: ContextualField;
    userNameField: ContextualField;
    titleAndNameHeader: { label: string; icon: string; };
    isSuperAdmin: boolean;
    keepSelectedUser: boolean;

    refresh: () => void;

    private _nameChronicle: Record;
    private _oldUserName: string;
    private _original: IUserViewModel;

    static $inject = [
        '$scope',
        'accountService',
        'loggerService',
        'Chronicle',
        'hotkeys',
        'userFactory',
        'userService',
        'storeService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _accountService: IAccountService,
        private _loggerService: ILoggerService,
        private _chronicle: Chronicle,
        private _hotkeys: any,
        private _userFactory: IUserFactory,
        private _userService: IUserService,
        private _storeService: IStoreService
    ) { }

    $onInit() {
        this.initMiniatureList();

        this._storeService.register(
            store => this.isSuperAdmin = store.authentication.value.isSuperAdmin,
            [ StoreElement.Authentication ]
        );

        this.modernDesign = true;

        this._hotkeys.add({
            combo: 'ctrl+z',
            description: 'Undo',
            allowIn: ['INPUT', 'SELECT', 'TEXTAREA'],
            callback(event, hotkey) {
                this.undo();
            },
        });

        this._hotkeys.add({
            combo: 'ctrl+y',
            description: 'Redo',
            allowIn: ['INPUT', 'SELECT', 'TEXTAREA'],
            callback(event, hotkey) {
                this.redo();
            },
        });

        this.initProperties();

        this._$scope.$watchGroup(
            [() => this.userPictureBase64, () => this.userSignatureBase64],
            () => {
                this.initProperties();
            });

        this._$scope.$watch(() => this.selectedItem, () => {
            if (this.selectedItem) {
                this._oldUserName = this.selectedItem.userName;
                this._original = angular.copy(this.selectedItem);
                // Load user picture
                this._userService
                    .getPicture(this.selectedItem.userName)
                    .then(data => {
                        if (data) {
                            this.userPictureName = 'picture.png';
                            this.userPictureBase64 = `data:image/false;base64,${data}`;
                        } else {
                            this.userPictureName = undefined;
                            this.userPictureBase64 = undefined;
                        }
                    })
                    .catch(error => {
                        this._loggerService.logError('Impossible to load user picture');
                    });
                // Load user signature
                this._userService
                    .getSignature(this.selectedItem.userName)
                    .then(data => {
                        if (data) {
                            this.userSignatureName = 'signature.png';
                            this.userSignatureBase64 = `data:image/false;base64,${data}`;
                        } else {
                            this.userSignatureName = undefined;
                            this.userSignatureBase64 = undefined;
                        }
                    })
                    .catch(error => {
                        this._loggerService.logError('Impossible to load user signature');
                    });
                this._nameChronicle = this._chronicle.record('userName', this.selectedItem, true);
            }
        });
    }

    initProperties() {
        this.userNameField = {
            modelName: 'userName',
            propertyType: PropertyType.Text,
            label: 'User name',
            propertyName: 'nameProperty',
            required: false,
            icon: 'fa-user',
            isValid: v => this.validateName()
        };

        this.domainNameField = {
            modelName: 'domainName',
            label: 'Domain name',
            propertyName: 'domainNameProperty',
            icon: 'fa-windows',
        };

        this.titleAndNameHeader = {
            label: 'Title, first and last name',
            icon: 'fa-id-badge'
        };

        this.titleField = {
            modelName: 'title',
            propertyType: PropertyType.Text,
            label: 'Title',
            propertyName: 'titleProperty',
            required: false,
            icon: 'fa-id-badge'
        };

        this.firstNameField = {
            modelName: 'firstName',
            propertyType: PropertyType.Text,
            label: 'First name',
            propertyName: 'firstNameProperty',
            required: false,
            icon: 'fa-id-badge'
        };

        this.lastNameField = {
            modelName: 'lastName',
            propertyType: PropertyType.Text,
            label: 'Last name',
            propertyName: 'lastNameProperty',
            required: false,
            icon: 'fa-id-badge'
        };

        this.emailField = {
            modelName: 'email',
            propertyType: PropertyType.Text,
            label: 'Email',
            propertyName: 'emailProperty',
            required: false,
            iconText: '@',
            isValid: v => this.validateEmail(),
        };

        this.phoneField = {
            modelName: 'phoneNumber',
            propertyType: PropertyType.Text,
            label: 'Phone number',
            propertyName: 'phoneProperty',
            required: false,
            icon: 'fa-phone'
        };

        this.companyField = {
            modelName: 'company',
            propertyType: PropertyType.Text,
            label: 'Company',
            propertyName: 'companyProperty',
            required: false,
            icon: 'fa-building-o'
        };

        this.departmentField = {
            modelName: 'department',
            propertyType: PropertyType.Text,
            label: 'Department',
            propertyName: 'departmentProperty',
            required: false,
            icon: 'fa-building-o'
        };

        this.jobTitleField = {
            modelName: 'jobTitle',
            propertyType: PropertyType.Text,
            label: 'Job title',
            propertyName: 'jobTitleProperty',
            required: false,
            icon: 'fa-building-o'
        };

        this.initialsField = {
            modelName: 'initials',
            propertyType: PropertyType.Text,
            label: 'Initials',
            propertyName: 'initialsProperty',
            required: false,
            iconText: 'AA'
        };

        this.pictureField = {
            modelName: 'picture',
            propertyType: PropertyType.File,
            label: 'Picture',
            propertyName: 'pictureProperty',
            required: false,
            icon: 'fa-picture-o',
            extraData: {
                filename: this.userPictureName,
                fileBase64: this.userPictureBase64,
                fileTypes: 'image/jpeg,image/png,image|gif'
            }
        };

        this.signatureField = {
            modelName: 'signature',
            propertyType: PropertyType.File,
            label: 'Signature',
            propertyName: 'signatureProperty',
            required: false,
            iconText: '🖆 ',
            extraData: {
                filename: this.userSignatureName,
                fileBase64: this.userSignatureBase64,
                fileTypes: 'image/jpeg,image/png,image|gif'
            }
        };

        this.isSuperAdminField = {
            modelName: 'isSuperAdmin',
            propertyType: PropertyType.Boolean,
            label: 'Super-administrator',
            placeholder: 'Super-administrator',
            propertyName: 'isSuperAdminProperty',
            required: false,
            icon: 'fa-user-md'
        };
        this.datePickerFormatField = {
            modelName: 'datePickerFormat',
            propertyType: PropertyType.Select,
            label: 'Date picker format',
            placeholder: 'Date picker format',
            propertyName: 'datePickerFormatProperty',
            required: false,
            icon: 'fa-calendar-o',
            extraData: {
                options: [
                    { key: '', value: '' },
                    { key: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
                    { key: 'DD-MM-YYYY', value: 'DD-MM-YYYY' },
                    { key: 'DD-MM-YY', value: 'DD-MM-YY' },
                    { key: 'MM-DD-YY', value: 'MM-DD-YY' },
                ]
            }
        };
    }

    private initMiniatureList(): void {
        this.usersTypes = this._userFactory.getMiniatureList();
    }

    resetPassword(): void {
        const userName = this.selectedItem.userName;

        this._accountService
            .resetPassword(userName)
            .then(data => {
                this._loggerService.logSuccess('Succesfully reset password');
            });
    }

    undo(): void {
        if (this.canUndo()) {
            this._nameChronicle.undo();
        }
    }

    canUndo(): boolean {
        return this._nameChronicle && this._nameChronicle.canUndo();
    }

    redo(): void {
        if (this.canRedo()) {
            this._nameChronicle.redo();
        }
    }

    canRedo(): boolean {
        return this._nameChronicle && this._nameChronicle.canRedo();
    }

    sync(): void {
        if (this.selectedItem) {
            const oldUserName = this.selectedItem.userName;
            this.selectedItem.userName = oldUserName;
            this._accountService
                .updateForUser(oldUserName, this.selectedItem)
                .then(data => {
                    this._loggerService.logSuccess(`Successfully synchronized user ${data.userName}`);
                })
                .catch(e => {
                    this._loggerService.logError('Error while saving user : ' + e.message);
                });
        }
    }

    save(): void {
        //const isUpdatingMyself =
        //    this._authenticationService.authentication.userName === this.oldUserName;
        const user = this.selectedItem;

        const promise = (user.guid === guidEmpty)
            ? this.create(user)
            : this.update(user);

        promise
            .then(data => {
                this.formController.$setPristine();
                this._loggerService.logSuccess(`Successfully saved user ${data.userName}`);
                this._oldUserName = data.userName;
                user.userName = data.userName;
                user.guid = data.guid;
                (user as any).adding = false;
                this._storeService.dispatchers().authentication.updateUserData({ datePickerFormat: data.datePickerFormat });
            })
            .then(() => {
                if (this.userPicture) {
                    return this._userService
                        .updateUserPicture(this.userPicture[0], user.userName)
                        .then(data => {
                            if (data) {
                                this.userPictureName = 'picture.png';
                                this.userPictureBase64 = `data:image/false;base64,${data}`;
                                user.pictureStyle = { 'background-image': `url('data:image/false;base64,${data}')` };
                            }
                            this._loggerService.logSuccess(`Successfully saved user picture`);
                        })
                        .catch(error => {
                            this._loggerService.logError(`Impossible to save user picture`);
                            this.formController.$setDirty();
                        });
                }
            })
            .then(() => {
                if (this.userSignature) {
                    return this._userService
                        .updateUserSignature(this.userSignature[0], user.userName)
                        .then(data => {
                            this.userSignatureName = 'signature.png';
                            this.userSignatureBase64 = `data:image/false;base64,${data}`;
                            this._loggerService.logSuccess(`Successfully saved user signature`);
                        })
                        .catch(error => {
                            this._loggerService.logError(`Impossible to save user signature`);
                            this.formController.$setDirty();
                        });
                }
            })
            // asked to be removed by #30919
            /*.then(() => {
                if (isUpdatingMyself) {
                    this._authenticationService.logOut();
                    this._$state.go('login');
                    this._loggerService.logSuccess(`Please login with new usernane ${this.oldUserName}`);
                }
            })*/
            .catch(() => {
                this._loggerService.logError('Error while saving user.');
            })
            .finally(() => {
                if (this.refresh) {
                    this.refresh();
                }
            });
    }

    private create(user: IUserViewModel) {
        return this._accountService.createOne(user, true);
    }

    private update(user: IUserViewModel) {
        return this._accountService.updateForUserAsBody(this._oldUserName, user);
    }

    cancel(): void {
        while (this._nameChronicle.canUndo()) {
            this._nameChronicle.undo();
        }

        // Save picture and signature before reset
        const userPicture = this.selectedItem.userPicture;
        const userSignature = this.selectedItem.userSignature;

        // Restore original user
        angular.copy(this._original, this.selectedItem);
        this.userPicture = undefined;
        this.userSignature = undefined;
        this.userPictureBase64 = undefined;
        this.userSignatureBase64 = undefined;
        this.initProperties();

        // Reassign values to picture and signature
        this.selectedItem.userPicture = userPicture;
        this.selectedItem.userSignature = userSignature;

        // Reset form state
        this.formController.$setPristine();

        // In profile, keep current user selected
        if (!this.keepSelectedUser) {
            this.selectedItem = null;
        }
    }

    validateName(): { [key: string]: any } {
        let hasDuplicate = false;
        let hasDomainDuplicate = false;
        const userName = this.formController[this.userNameField.propertyName].$viewValue;
        if (userName && this.users) {
            hasDuplicate = _.some(this.users, u => u.userName
                && u.userName.toLowerCase() === userName.toLowerCase()
                && this.selectedItem !== u);
            let domainViewValue = this.formController.domainNameProperty.$viewValue;
            domainViewValue = domainViewValue ? domainViewValue.toLowerCase() : null;
            hasDomainDuplicate = _.some(this.users, u => {
                return u && u.domainName
                    && u.domainName.toLowerCase() === domainViewValue
                    && this.selectedItem !== u;
            });
            this.formController.nameProperty.$setValidity('duplicate', !hasDuplicate);
            this.formController.domainNameProperty.$setValidity('duplicate', !hasDomainDuplicate);
        }
        return Helper.getDictionnary<boolean>([
            { key: 'duplicate', value: !hasDuplicate && !hasDomainDuplicate }
        ]);
    }

    validateEmail(): { [key: string]: any } {
        const userEmails = this.formController[this.emailField.propertyName].$viewValue as string;
        const isEmailValid = validateEmailList(userEmails);
        this.formController.emailProperty.$setValidity('invalid', isEmailValid);
        return Helper.getDictionnary<boolean>([{ key: 'invalid', value: !isEmailValid }]);
    }
}

export const wtUserContextual: ComponentDefinition = {
    componentId: 'wtUserContextual',
    controller: UserContextualController,
    template: require('./userContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedItem: '=',
        formController: '=',
        users: '<?',
        refresh: '&?',
        keepSelectedUser: '@?'
    },
};